import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

/**
 * Keep `document.title` describing the current route (WCAG 2.1 — 2.4.2 Page
 * Titled, Level A).
 *
 * As an SPA the app shipped a single static `<title>` that never changed, so
 * every route announced identically. The title is the first thing a screen
 * reader reads on navigation and the only thing distinguishing entries in the
 * browser's tab strip, history and window switcher — with one value, none of
 * those work.
 *
 * Route-pattern driven rather than per-page hook calls: 30+ routes each
 * remembering to set their own title is a rule that decays, and a missed one
 * silently inherits whatever the previous page set. One ordered table is
 * greppable and fails visibly.
 */

/** Everything a member of the public can reach lives under this prefix. */
const PUBLIC_RUNTIME = /^\/ext(\/|$)/;

/** BMO's customer-facing brand — the title the public runtime wears. */
const PUBLIC_BRAND_KEY = 'app.publicTitle';

/** First match wins, so put specific patterns before their prefixes. */
const TITLE_PATTERNS: ReadonlyArray<[RegExp, string]> = [
  [/^\/auth\/logout/, 'a11y.pageTitles.signOut'],
  [/^\/auth\/logged-out/, 'a11y.pageTitles.signedOut'],
  [/^\/auth\/dev-login/, 'a11y.pageTitles.signIn'],
  [/^\/auth\/error/, 'a11y.pageTitles.signInError'],

  [/^\/approver\/audit-history/, 'a11y.pageTitles.auditHistory'],
  [/^\/approver\/archive/, 'a11y.pageTitles.archive'],
  [/^\/approver\/workflows\/[^/]+\/preview/, 'a11y.pageTitles.preview'],
  [/^\/approver\/workflows\/[^/]+\/versions\/[^/]+/, 'a11y.pageTitles.versionDetails'],
  [/^\/approver\/workflows\/[^/]+\/versions/, 'a11y.pageTitles.versionHistory'],
  [/^\/approver\/workflows\/[^/]+\/steps\/[^/]+\/config/, 'a11y.pageTitles.stepConfig'],
  [/^\/approver\/workflows\/[^/]+/, 'a11y.pageTitles.workflowReview'],
  [/^\/approver\/dashboard/, 'a11y.pageTitles.approverDashboard'],

  [/^\/originator\/audit-history/, 'a11y.pageTitles.auditHistory'],
  [/^\/originator\/archive/, 'a11y.pageTitles.archive'],
  [/^\/originator\/custom-elements/, 'a11y.pageTitles.customElements'],
  [/^\/originator\/workflows\/[^/]+\/preview/, 'a11y.pageTitles.preview'],
  [/^\/originator\/workflows\/[^/]+\/pdf-mapping/, 'a11y.pageTitles.pdfMapping'],
  [/^\/originator\/workflows\/[^/]+\/signature-placement/, 'a11y.pageTitles.signaturePlacement'],
  [/^\/originator\/workflows\/[^/]+\/versions\/[^/]+/, 'a11y.pageTitles.versionDetails'],
  [/^\/originator\/workflows\/[^/]+\/versions/, 'a11y.pageTitles.versionHistory'],
  [/^\/originator\/workflows\/[^/]+\/steps\/[^/]+\/edit/, 'a11y.pageTitles.formBuilder'],
  [/^\/originator\/workflows\/[^/]+\/steps\/[^/]+\/config/, 'a11y.pageTitles.stepConfig'],
  [/^\/originator\/workflows\/[^/]+/, 'a11y.pageTitles.workflowDetails'],
  [/^\/originator\/dashboard/, 'a11y.pageTitles.originatorDashboard'],

  [/^\/advisor\/workflows\/[^/]+\/applications/, 'a11y.pageTitles.application'],
  // The entry resolver shares the application title rather than getting its own:
  // it is a transient bounce into the ID-scoped form, so a distinct title would
  // only ever flash before the redirect. Unmapped, it inherited the not-found
  // fallback — which is what an advisor arriving from ISAM actually saw.
  [/^\/advisor\/entry/, 'a11y.pageTitles.application'],
  [/^\/advisor\/dashboard/, 'a11y.pageTitles.advisorDashboard'],

  // The public customer runtime (spec §2 "Public user", §12). Named with BMO's
  // customer-facing brand rather than a page name: there is exactly one page
  // under /ext, and "Application" adds nothing a customer can use. Unmapped it
  // inherited the not-found fallback, so someone handed a WORKING link read
  // "Page not found" in their tab while the form loaded underneath.
  [/^\/ext\/workflows\/[^/]+\/new/, PUBLIC_BRAND_KEY],

  [/^\/health/, 'a11y.pageTitles.health'],
  [/^\/$/, 'a11y.pageTitles.home'],
];

/** Exported for the test — the mapping is the part worth pinning. */
export function titleKeyForPath(pathname: string): string {
  for (const [pattern, key] of TITLE_PATTERNS) {
    if (pattern.test(pathname)) return key;
  }
  return 'a11y.pageTitles.notFound';
}

/**
 * The whole `document.title`, suffix included. Exported for the test.
 *
 * Everything under /ext is the only part of the app a member of the public ever
 * sees, and "BMO Workflow Platform" is an internal name that means nothing to
 * them — so that branch carries the customer-facing brand instead. A mistyped
 * /ext link still keeps its "Page not found" page name, so the tab never claims
 * a form loaded when none did.
 */
export function documentTitleFor(pathname: string, t: (key: string) => string): string {
  const brandKey = PUBLIC_RUNTIME.test(pathname) ? PUBLIC_BRAND_KEY : 'app.title';
  const key = titleKeyForPath(pathname);
  // The public form's page name IS the brand; naming it as well would read
  // "BMO-SmartForms — BMO-SmartForms".
  if (key === brandKey) return t(key);
  // Suffixed with the product name so the tab strip still identifies the app
  // when the page name alone is generic ("Archive", "Preview").
  return `${t(key)} — ${t(brandKey)}`;
}

export function useDocumentTitle(): void {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.title = documentTitleFor(pathname, t);
    // i18n.language so the title re-resolves on a language switch rather than
    // sitting in the outgoing locale until the next navigation.
  }, [pathname, t, i18n.language]);
}

/** Zero-render component wrapper — lets the hook be mounted declaratively
 *  inside the Router without giving App a body it doesn't otherwise need. */
export function DocumentTitle(): null {
  useDocumentTitle();
  return null;
}
